import { TextField, TextFieldProps } from '@mui/material';
import { Control, Controller, FieldPath, FieldValues, UseFormRegister } from 'react-hook-form';

export interface TextInputProps<TFieldValues extends FieldValues = FieldValues> {
  name: FieldPath<TFieldValues>;
  control?: Control<TFieldValues>;
  register?: UseFormRegister<TFieldValues>;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  helperText?: string;
  placeholder?: string;
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url';
  inputProps?: TextFieldProps['inputProps'];
  error?: boolean;
}

export function TextInput<TFieldValues extends FieldValues = FieldValues>({
  name,
  control,
  register,
  label,
  disabled,
  required,
  helperText,
  placeholder,
  type = 'text',
  inputProps,
  error,
  ...textFieldProps
}: TextInputProps<TFieldValues> & Omit<TextFieldProps, 'name' | 'control' | 'error' | 'helperText'>) {
  if (register) {
    // Use register pattern
    const { onChange, onBlur, name: fieldName, ref } = register(name);
    return (
      <TextField
        {...textFieldProps}
        name={fieldName}
        inputRef={ref}
        onChange={onChange}
        onBlur={onBlur}
        fullWidth
        label={label}
        type={type}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        error={error}
        helperText={helperText}
        inputProps={inputProps}
      />
    );
  }

  if (!control) {
    throw new Error('Either control or register must be provided to TextInput');
  }

  // Use Controller pattern
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <TextField
          {...field}
          {...textFieldProps}
          fullWidth
          label={label}
          type={type}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          error={!!fieldState.error}
          helperText={fieldState.error?.message || helperText}
          inputProps={inputProps}
        />
      )}
    />
  );
}